import { useState } from 'react';

import { useMutation } from '@tanstack/react-query';

import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  Textarea,
  IconButton,
  Tooltip,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';

import { ChatIcon } from '@chakra-ui/icons';

import { apiUpdateCallListItem } from 'api';

import type { APICallListData } from 'types/api.types';

const ItemNote = ({ item }: { item: APICallListData }) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const toast = useToast();

  const [note, setNote] = useState(item.notes || '');
  const [originalNote] = useState(item.notes || '');

  const updateNoteMutation = useMutation({
    mutationFn: () => {
      return apiUpdateCallListItem(item.id, { notes: note });
    },
    onSuccess: (data) => {
      item.notes = data.notes;

      toast({
        title: 'Note updated',
        status: 'success',
        duration: 3000,
        isClosable: true,
        position: 'top',
      });

      onClose();
    },
    onError: (error) => {
      toast({
        title: 'Error updating note',
        description:
          error instanceof Error ? error.message : 'Unknown error occurred',
        status: 'error',
        duration: 5000,
        isClosable: true,
        position: 'top',
      });
    },
  });

  const handleSave = () => {
    if (note === originalNote) {
      onClose();
      return;
    }

    updateNoteMutation.mutate();
  };

  return (
    <>
      <Tooltip label="Add/Edit Notes" hasArrow placement="top">
        <IconButton
          aria-label="Add or edit notes"
          icon={<ChatIcon />}
          size="sm"
          colorScheme="teal"
          variant="ghost"
          onClick={onOpen}
          ml={2}
        />
      </Tooltip>

      <Modal isOpen={isOpen} onClose={onClose} size="2xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Edit Note</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              variant="main"
              minH="300px"
              resize="vertical"
            />
          </ModalBody>

          <ModalFooter>
            <Button
              variant="outline"
              mr={3}
              onClick={onClose}
              isDisabled={updateNoteMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              colorScheme="blue"
              onClick={handleSave}
              isLoading={updateNoteMutation.isPending}
              loadingText="Saving..."
            >
              Save
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};

export default ItemNote;
